import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { safeErrorText } from "../utils/dashboardStatus.utils";

export function useCachedDashboardQuery<T>(
  enabled: boolean,
  getCached: () => T | null,
  fetcher: (force?: boolean) => Promise<T>,
): {
  data: T | null;
  loading: boolean;
  refreshing: boolean;
  error: string | null;
  reload: (force?: boolean) => Promise<T | null>;
} {
  const initial = useMemo(() => (enabled ? getCached() : null), [enabled, getCached]);
  const [data, setData] = useState<T | null>(initial);
  const [loading, setLoading] = useState(enabled && initial == null);
  const [refreshing, setRefreshing] = useState(enabled && initial != null);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const reload = useCallback(
    async (force = false) => {
      if (!enabled) {
        setData(null);
        setLoading(false);
        setRefreshing(false);
        return null;
      }

      const cached = getCached();
      if (cached != null && data == null) setData(cached);
      if (cached != null || data != null) {
        setRefreshing(true);
        setLoading(false);
      } else {
        setLoading(true);
      }
      setError(null);

      try {
        const next = await fetcher(force);
        if (!mountedRef.current) return next;
        setData(next);
        return next;
      } catch (loadError) {
        if (mountedRef.current) setError(safeErrorText(loadError, "Something went wrong."));
        return null;
      } finally {
        if (mountedRef.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [data, enabled, fetcher, getCached],
  );

  useEffect(() => {
    void reload(false);
  }, [reload]);

  return { data, loading, refreshing, error, reload };
}
